import React, { createContext, useContext, useMemo } from 'react';
import { useAuth } from './AuthContext';

/**
 * Alpha/Beta Mode Flag
 * When true, all users get unlimited access to all features regardless of plan
 * Set to false when going to production to enforce tier restrictions
 * 
 * IMPORTANT: Keep this in sync with server/config/tiers.js
 */
const ALPHA_BETA_MODE = true;

/**
 * Tier/Plan Configuration (mirrors server/config/tiers.js)
 */
const TIER_CONFIG = {
  free: {
    name: 'Indie Filmmaker',
    displayName: 'Free',
    limits: {
      maxProjects: 2,
      maxCollaboratorsPerProject: 10,
      maxStorageMB: 500,
      maxScriptsPerProject: 5,
      maxAnalysesPerMonth: 3
    },
    features: {
      sceneBreakdowns: true,
      shotLists: true,
      taskBoards: true,
      scheduleExports: true,
      callSheets: true,
      aiLoglines: false,
      aiInsights: false,
      aiAnalysis: false,
      advancedScheduling: false,
      assetVersioning: false,
      prioritySupport: false
    }
  },
  studio: {
    name: 'Studio',
    displayName: 'Studio',
    limits: {
      maxProjects: -1,
      maxCollaboratorsPerProject: -1,
      maxStorageMB: 10000,
      maxScriptsPerProject: -1,
      maxAnalysesPerMonth: -1
    },
    features: {
      sceneBreakdowns: true,
      shotLists: true,
      taskBoards: true,
      scheduleExports: true,
      callSheets: true,
      aiLoglines: true,
      aiInsights: true,
      aiAnalysis: true,
      advancedScheduling: true,
      assetVersioning: true,
      prioritySupport: true
    }
  },
  student: {
    name: 'Student Access',
    displayName: 'Student',
    limits: {
      maxProjects: -1,
      maxCollaboratorsPerProject: -1,
      maxStorageMB: 5000,
      maxScriptsPerProject: -1,
      maxAnalysesPerMonth: -1
    },
    features: {
      sceneBreakdowns: true,
      shotLists: true,
      taskBoards: true,
      scheduleExports: true,
      callSheets: true,
      aiLoglines: true,
      aiInsights: true,
      aiAnalysis: true,
      advancedScheduling: true,
      assetVersioning: true,
      prioritySupport: false
    }
  }
};

const TierContext = createContext(null);

export const TierProvider = ({ children }) => {
  const { user } = useAuth();
  
  const tierInfo = useMemo(() => {
    const plan = user?.plan || 'free';
    const config = TIER_CONFIG[plan] || TIER_CONFIG.free;
    
    return {
      plan,
      planName: config.name,
      displayName: config.displayName,
      limits: config.limits,
      features: config.features,
      
      // Helper methods - respect ALPHA_BETA_MODE
      hasFeature: (featureName) => ALPHA_BETA_MODE ? true : config.features[featureName] === true,
      getLimit: (limitKey) => ALPHA_BETA_MODE ? -1 : (config.limits[limitKey] ?? 0),
      isUnlimited: (limitKey) => ALPHA_BETA_MODE ? true : config.limits[limitKey] === -1,
      isLimitReached: (limitKey, currentValue) => {
        if (ALPHA_BETA_MODE) return false;
        const limit = config.limits[limitKey];
        if (limit === -1) return false;
        return currentValue >= limit;
      },
      
      // Alpha/Beta mode info
      isAlphaBeta: ALPHA_BETA_MODE,
      
      // Plan checks
      isFree: plan === 'free',
      isStudio: plan === 'studio',
      isStudent: plan === 'student',
      isPaid: plan === 'studio',
      
      // Upgrade info
      canUpgrade: plan === 'free',
      upgradeUrl: '/settings?section=billing'
    };
  }, [user?.plan]);
  
  return (
    <TierContext.Provider value={tierInfo}>
      {children}
    </TierContext.Provider>
  );
};

export const useTier = () => {
  const context = useContext(TierContext);
  if (!context) {
    throw new Error('useTier must be used within a TierProvider');
  }
  return context;
};

export default TierContext;
